import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STATUS_STYLES, STATUS_LABEL, CATEGORIES } from "../lib/constants";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { PlusCircle, Loader2, Inbox, XCircle, AlertTriangle, ShieldCheck, Check, X, Trash2 } from "lucide-react";

const CANCELLABLE = ["open", "accepted", "in_progress"];

function VouchInbox() {
  const [reqs, setReqs] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/vouch/incoming"); setReqs(data); } catch (e) { console.error("Failed to load vouch requests", e); }
  };
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try {
      await api.post(`/vouch/${id}/${action}`);
      toast.success(action === "approve" ? "Neighbor verified!" : "Request declined");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  if (reqs.length === 0) return null;
  return (
    <div data-testid="vouch-inbox" className="bg-white rounded-3xl border-2 border-primary/30 shadow-soft p-6 mb-6">
      <p className="font-heading font-extrabold text-lg text-[#1A1D20] mb-1 flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /> Verify a neighbor</p>
      <p className="text-sm text-[#6B7280] mb-4">A neighbor asked you to confirm their identity and address.</p>
      <div className="space-y-3">
        {reqs.map((r) => (
          <div key={r.id} data-testid={`vouch-req-${r.id}`} className="flex items-center justify-between gap-3 bg-muted rounded-2xl p-4">
            <div className="min-w-0">
              <p className="font-bold text-[#1A1D20]">{r.requester_alias}</p>
              <p className="text-xs text-[#6B7280] truncate">{r.address}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button data-testid={`vouch-approve-${r.id}`} onClick={() => act(r.id, "approve")} size="sm" className="rounded-full font-bold bg-accent hover:opacity-90"><Check size={15} className="mr-1" /> Verify</Button>
              <Button data-testid={`vouch-decline-${r.id}`} onClick={() => act(r.id, "decline")} size="sm" variant="ghost" className="rounded-full font-semibold text-[#B23B3B] hover:bg-[#FBE9E9]"><X size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PostedRow({ e, onCancel, onDelete }) {
  const cat = CATEGORIES.find((c) => c.value === e.category)?.label || e.category;
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasFee = ["accepted", "in_progress"].includes(e.status);
  const isOpen = e.status === "open";

  const confirm = async () => {
    setBusy(true);
    await onCancel(e.id);
    setBusy(false);
    setOpen(false);
  };
  const confirmDelete = async () => {
    setBusy(true);
    await onDelete(e.id);
    setBusy(false);
    setDeleteOpen(false);
  };

  return (
    <div data-testid={`dash-posted-${e.id}`} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-4 hover:shadow-lift transition-all">
      <div className="flex items-center justify-between gap-3">
        <Link to={`/errand/${e.id}`} className="min-w-0 flex-1">
          <p className="font-bold text-[#1A1D20] truncate">{e.title}</p>
          <p className="text-xs text-[#6B7280]">{cat} · {e.location}</p>
        </Link>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-heading font-extrabold text-primary">${Number(e.budget).toFixed(0)}</span>
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${STATUS_STYLES[e.status]}`}>{STATUS_LABEL[e.status]}</span>
        </div>
      </div>
      {(isOpen || CANCELLABLE.includes(e.status)) && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
          {isOpen ? (
            <button type="button" data-testid={`delete-errand-btn-${e.id}`} onClick={() => setDeleteOpen(true)} aria-label="Delete errand"
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#B23B3B] bg-[#FBE9E9]/60 hover:bg-[#FBE9E9] transition-colors">
              <Trash2 size={16} />
            </button>
          ) : (
            <Button data-testid={`cancel-errand-btn-${e.id}`} onClick={() => setOpen(true)} variant="ghost" size="sm"
              className="rounded-full font-semibold text-[#B23B3B] hover:bg-[#FBE9E9] h-9">
              <XCircle size={15} className="mr-1.5" /> Cancel Errand
            </Button>
          )}
        </div>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="rounded-3xl" data-testid={`delete-dialog-${e.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this errand?</AlertDialogTitle>
            <AlertDialogDescription>
              "{e.title}" will be permanently removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`delete-dialog-no-${e.id}`} className="rounded-full font-semibold">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid={`delete-dialog-yes-${e.id}`} onClick={(ev) => { ev.preventDefault(); confirmDelete(); }} disabled={busy}
              className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">
              {busy ? <Loader2 className="animate-spin" size={16} /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="rounded-3xl" data-testid={`cancel-dialog-${e.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Cancel this errand?</AlertDialogTitle>
            <AlertDialogDescription>
              "{e.title}" will be cancelled and removed from your list.
              {e.runner_name ? ` ${e.runner_name} will be notified that you cancelled.` : ""} This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {hasFee && (
            <div data-testid={`cancel-fee-notice-${e.id}`} className="flex items-start gap-2 bg-[#FFF4EE] border border-[#FFD9C7] rounded-2xl p-3 text-sm text-[#8A3D1E]">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>A <strong>$7 cancellation fee</strong> applies because a runner already accepted. You'll be taken to secure checkout.</span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`cancel-dialog-no-${e.id}`} className="rounded-full font-semibold">Keep errand</AlertDialogCancel>
            <AlertDialogAction data-testid={`cancel-dialog-yes-${e.id}`} onClick={(ev) => { ev.preventDefault(); confirm(); }} disabled={busy}
              className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">
              {busy ? <Loader2 className="animate-spin" size={16} /> : hasFee ? "Pay $7 & cancel" : "Yes, cancel it"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AcceptedRow({ e }) {
  const cat = CATEGORIES.find((c) => c.value === e.category)?.label || e.category;
  const cancelled = e.status === "cancelled";
  return (
    <Link to={`/errand/${e.id}`} data-testid={`dash-accepted-${e.id}`}
      className={`block rounded-2xl border shadow-soft p-4 hover:shadow-lift transition-all ${cancelled ? "bg-[#FBF3F3] border-[#F1D5D5]" : "bg-white border-gray-100"}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-[#1A1D20] truncate">{e.title}</p>
          <p className="text-xs text-[#6B7280]">{cat} · {e.location}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-heading font-extrabold text-primary">${Number(e.budget).toFixed(0)}</span>
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${STATUS_STYLES[e.status]}`}>{STATUS_LABEL[e.status]}</span>
        </div>
      </div>
      {cancelled && (
        <p data-testid={`cancelled-notice-${e.id}`} className="mt-2.5 flex items-center gap-1.5 text-xs font-semibold text-[#B23B3B]">
          <AlertTriangle size={13} /> {e.requester_name} cancelled this errand.
        </p>
      )}
    </Link>
  );
}

function Section({ title, count, empty, children }) {
  return (
    <div>
      <h2 className="font-heading font-extrabold text-xl text-[#1A1D20] mb-3">{title} <span className="text-[#9CA3AF] text-base">({count})</span></h2>
      {count === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 text-center text-[#9CA3AF]">
          <Inbox className="mx-auto mb-2" /> {empty}
        </div>
      ) : (
        <div className="space-y-3">{children}</div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = async () => { const r = await api.get("/errands/mine"); setData(r.data); };
  useEffect(() => { load(); }, []);

  const cancelErrand = async (id) => {
    try {
      const { data } = await api.post(`/errands/${id}/cancel`, { origin_url: window.location.origin });
      if (data.fee_required && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.success("Errand cancelled");
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const deleteErrand = async (id) => {
    try {
      await api.delete(`/errands/${id}`);
      toast.success("Errand deleted");
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  if (!data) return <div className="flex justify-center py-20 text-[#6B7280]"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1A1D20]">Your dashboard</h1>
          <p className="text-[#6B7280] mt-1">Track everything you've posted and picked up.</p>
        </div>
        <Link to="/post"><Button data-testid="dash-post-btn" className="rounded-full font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
          <PlusCircle size={18} className="mr-2" /> Request an Errand
        </Button></Link>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {[
          { label: "Posted", value: data.posted.length },
          { label: "Accepted", value: data.accepted.length },
          { label: "Completed", value: user?.errands_completed || 0 },
        ].map((s) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6">
            <p className="text-xs font-semibold uppercase text-[#9CA3AF]">{s.label}</p>
            <p className="font-heading font-black text-3xl text-[#1A1D20] mt-1">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <VouchInbox />

      <div className="grid md:grid-cols-2 gap-8">
        <Section title="Errands I posted" count={data.posted.length} empty="You haven't posted any errands yet.">
          {data.posted.map((e) => <PostedRow key={e.id} e={e} onCancel={cancelErrand} onDelete={deleteErrand} />)}
        </Section>
        <Section title="Errands I'm running" count={data.accepted.length} empty="You haven't accepted any errands yet.">
          {data.accepted.map((e) => <AcceptedRow key={e.id} e={e} />)}
        </Section>
      </div>
    </div>
  );
}
